<script lang="ts">
  /**
   * A ranking of a few players (1.7.0): who is first, by how much, and who
   * won. Built for playing together on one device (a race in turns, a round
   * of solo runs on one seed) and for any results that compare two to N
   * players.
   *
   * - **In rank order.** The rows are drawn by `rank` (a stable sort, so
   *   rows that share a rank keep the game's order). The game decides the
   *   ranks; `competitionRanks` is the usual rule ("1224").
   * - **A tie says so.** Rows with the same rank are a tie: the rank reads
   *   "=2" on screen and "Tied for rank 2" to a screen reader (`formatRank`
   *   replaces the spoken words).
   * - **A winner is marked by more than colour**: a trophy and the word
   *   (`winnerLabel`, the shell's "Winner" by default) beside the value, and
   *   a heavier outline on the row. A shared win marks every winner. In the
   *   `compact` size the word is for screen readers only and the trophy
   *   stays.
   * - **A list, read as one.** An `<ol>` in a section named by `label` (shown
   *   over it), each row its rank, its name (with an optional `tag` chip: "Next",
   *   "You"), an optional `detail` line, and the value.
   *
   * `tone`: `page` (the default) for a page or a sheet, the rows on the card
   * colour inside a line (`--ui-line`); `deep` for the deep gradient
   * (`ResultsTemplate`), the rows a wash of the on-deep colour and the
   * trophy gold. `size`: `full` (the default) or `compact`, one line a row
   * with no detail (a hand-off).
   *
   * Test ids: `testid` (default `ui-standings`) on the root; each row its
   * own `testid`, and `data-rank` / `data-winner` on every row.
   */
  import { shellText } from '../i18n.svelte.js';
  import type { StandingsRow } from '../templates/playTypes.js';
  import Icon from './Icon.svelte';

  interface Props {
    readonly rows: readonly StandingsRow[];
    readonly label?: string | undefined;
    readonly winnerLabel?: string | undefined;
    /** The rank as a screen reader hears it. Default the shell's "Rank 2" / "Tied for rank 2". */
    readonly formatRank?: ((rank: number, tied: boolean) => string) | undefined;
    readonly tone?: 'page' | 'deep';
    readonly size?: 'full' | 'compact';
    readonly testid?: string | undefined;
  }
  const {
    rows,
    label,
    winnerLabel,
    formatRank,
    tone = 'page',
    size = 'full',
    testid = 'ui-standings',
  }: Props = $props();

  const labelId = $props.id();

  const ordered = $derived(
    rows
      .map((row, index) => ({ row, index }))
      .sort((a, b) => a.row.rank - b.row.rank || a.index - b.index)
      .map((entry) => entry.row),
  );

  function tied(rank: number): boolean {
    return rows.filter((row) => row.rank === rank).length > 1;
  }

  function spokenRank(rank: number): string {
    const isTied = tied(rank);
    if (formatRank !== undefined) return formatRank(rank, isTied);
    return isTied ? shellText('standingsTied', { rank }) : shellText('standingsRank', { rank });
  }

  const winnerWord = $derived(winnerLabel ?? shellText('standingsWinner'));
</script>

<section
  class="ui-standings ui-standings--{tone} ui-standings--{size}"
  aria-labelledby={label === undefined ? undefined : labelId}
  data-testid={testid}
>
  {#if label !== undefined}
    <p class="ui-standings-label" id={labelId}>{label}</p>
  {/if}
  <ol class="ui-standings-list">
    {#each ordered as row (row.id)}
      <li
        class="ui-standings-row"
        class:ui-standings-row--winner={row.winner === true}
        data-rank={row.rank}
        data-winner={row.winner === true ? 'true' : undefined}
        data-testid={row.testid}
      >
        <span class="ui-standings-rank" aria-hidden="true"
          >{tied(row.rank) ? '=' : ''}{row.rank}</span
        >
        <span class="ui-standings-hidden">{spokenRank(row.rank)}:</span>
        <span class="ui-standings-who">
          <span class="ui-standings-name-line">
            <span class="ui-standings-name">{row.name}</span>
            {#if row.tag !== undefined}
              <span class="ui-standings-tag">{row.tag}</span>
            {/if}
          </span>
          {#if row.detail !== undefined && size === 'full'}
            <span class="ui-standings-detail">{row.detail}</span>
          {/if}
        </span>
        {#if row.winner === true}
          <span class="ui-standings-winner">
            <Icon
              name="trophy"
              size={size === 'compact' ? 16 : 18}
              color={tone === 'deep' ? 'var(--ui-gold)' : 'var(--ui-accent)'}
            />
            <span class:ui-standings-hidden={size === 'compact'}>{winnerWord}</span>
          </span>
        {/if}
        <b class="ui-standings-value">{row.value}</b>
      </li>
    {/each}
  </ol>
</section>

<style>
  .ui-standings {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-2);
    min-width: 0;
    text-align: left;
    font-family: var(--ui-font);
  }

  .ui-standings-label {
    margin: 0;
    font: var(--ui-text-eyebrow);
    letter-spacing: var(--ui-tracking-eyebrow);
    text-transform: uppercase;
  }

  .ui-standings-list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-2);
  }

  .ui-standings-row {
    display: flex;
    align-items: center;
    gap: var(--ui-space-3);
    min-height: 3.25rem;
    padding: 0.55rem 0.9rem;
    border: 2px solid transparent;
    border-radius: var(--ui-radius-md);
  }

  .ui-standings--compact .ui-standings-row {
    min-height: 2.75rem;
    padding: 0.35rem 0.75rem;
  }

  .ui-standings-rank {
    flex: 0 0 1.75rem;
    font: var(--ui-text-heading);
    font-variant-numeric: tabular-nums;
    text-align: center;
  }

  .ui-standings-who {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .ui-standings-name-line {
    display: flex;
    align-items: center;
    gap: var(--ui-space-2);
    min-width: 0;
  }

  .ui-standings-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font: var(--ui-text-body);
    font-weight: 700;
  }

  .ui-standings-tag {
    flex: 0 0 auto;
    padding: 0.1rem 0.5rem;
    border-radius: var(--ui-radius-pill);
    font: var(--ui-text-eyebrow);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .ui-standings-detail {
    font: var(--ui-text-caption);
  }

  .ui-standings-winner {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    font: var(--ui-text-caption);
    font-weight: 800;
  }

  .ui-standings-value {
    flex: 0 0 auto;
    font: var(--ui-text-heading);
    font-variant-numeric: tabular-nums;
  }

  .ui-standings-row--winner .ui-standings-value {
    font-weight: 800;
  }

  .ui-standings-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* On a page or a sheet: rows on the card colour, ink, the accent for a winner. */
  .ui-standings--page {
    color: var(--ui-ink);
  }

  .ui-standings--page .ui-standings-label,
  .ui-standings--page .ui-standings-rank,
  .ui-standings--page .ui-standings-detail {
    color: var(--ui-ink-2);
  }

  /* A hairline round each row, so the rows read as rows where the card is
     the ground's own colour (the house's light appearance). Decoration, not a
     gated pair: the rank, the words and the order carry the meaning. */
  .ui-standings--page .ui-standings-row {
    border-color: var(--ui-line);
    background: var(--ui-card);
  }

  .ui-standings--page .ui-standings-row--winner {
    border-color: var(--ui-accent);
  }

  .ui-standings--page .ui-standings-tag {
    background: var(--ui-surface-2);
    color: var(--ui-ink);
  }

  /* On the deep gradient: the facts panel's wash (16% on-deep, a gated pair), the outline
     on-deep, a tag on the gradient's dark stop as the results tag is (gated). */
  .ui-standings--deep {
    color: var(--ui-on-deep);
  }

  .ui-standings--deep .ui-standings-row {
    background: color-mix(in srgb, var(--ui-on-deep) 16%, transparent);
  }

  .ui-standings--deep .ui-standings-row--winner {
    border-color: var(--ui-on-deep);
  }

  .ui-standings--deep .ui-standings-tag {
    background: var(--ui-deep-to);
  }
</style>
